import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../utils/notificationUtils';
import './DailyChecklistTab.css';
import SpecialRequestsReadOnlyModal from '../../SpecialRequestsReadOnlyModal';

const DailyChecklistTab = ({
    inpatientId, // kept for context/future backend calls if needed
    visitDate,
    lengthOfStay = 1,
    services = [],
    clinicServices = [],
    specialRequestsNote = '',
    onAddService,
    onUpdateService,
    onRemoveService,
    onAutoSave,
    readOnly = false,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    // State
    const [activeDay, setActiveDay] = useState(1);
    const [activeSubTab, setActiveSubTab] = useState('scheduled'); // scheduled | fulfilled
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [savingKey, setSavingKey] = useState(null); // identifier for per-action saving UX
    const [showSpecialRequests, setShowSpecialRequests] = useState(false);

    // Helpers
    const getDayDate = (dayIndex) => {
        if (!visitDate) return null;
        // Parse as local YYYY-MM-DD to avoid timezone shifts from full datetime strings
        const [datePart] = String(visitDate).split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        date.setDate(date.getDate() + (dayIndex - 1));
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Daily Services should reveal days one-by-one as they arrive.
    // Show days from admission up to today (inclusive), capped by the planned stay length.
    const totalDays = useMemo(() => {
        const stayDays = Math.max(1, parseInt(lengthOfStay || 1));
        if (!visitDate) return stayDays;

        const [datePart] = String(visitDate).split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const start = new Date(year, month - 1, day);
        start.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const elapsedDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));
        const revealedDays = Math.max(1, elapsedDays + 1);
        return Math.min(revealedDays, stayDays);
    }, [visitDate, lengthOfStay]);

    // Keep active day within the revealed day range
    useEffect(() => {
        setActiveDay(prev => (prev > totalDays ? totalDays : prev));
    }, [totalDays]);

    const activeDateString = useMemo(() => getDayDate(activeDay), [activeDay, visitDate]);

    const dayServices = useMemo(() => {
        if (!activeDateString) return [];
        return (services || []).filter(s => (s.due_date || null) === activeDateString);
    }, [services, activeDateString]);

    const scheduledServices = useMemo(
        () => dayServices.filter(s => !s.is_fulfilled),
        [dayServices]
    );
    const fulfilledServices = useMemo(
        () => dayServices.filter(s => !!s.is_fulfilled),
        [dayServices]
    );

    // Search options (startsWith)
    const filteredSearchOptions = useMemo(() => {
        if (!searchQuery) return [];
        const lowerQ = searchQuery.toLowerCase();
        return (clinicServices || []).filter(s => {
            const nameEn = s.service_name_en?.toLowerCase() || '';
            const nameAr = s.service_name_ar?.toLowerCase() || '';
            return nameEn.startsWith(lowerQ) || nameAr.startsWith(lowerQ);
        }).slice(0, 10);
    }, [searchQuery, clinicServices]);

    const getIdentifier = (s) => {
        if (s?.uniqueKey) return s.uniqueKey;
        if (s?.inpatient_visit_detail_id) return `vd-${s.inpatient_visit_detail_id}`;
        return s?.id;
    };

    const handleCompleted = async (serviceRow) => {
        if (readOnly) return;
        if (!onUpdateService || !onAutoSave) return;

        const identifier = getIdentifier(serviceRow);
        setSavingKey(identifier);

        // Optimistic update
        onUpdateService(identifier, { is_fulfilled: true });

        const ok = await onAutoSave(true);
        if (!ok) {
            // Rollback
            onUpdateService(identifier, { is_fulfilled: false });
            showErrorToast(t('receptionInpatients.details.toasts.saveFailed', { defaultValue: 'Save failed' }));
        }

        setSavingKey(null);
    };

    const handleSelectFulfilledService = async (service) => {
        if (readOnly) return;
        if (!onAddService || !onAutoSave) return;

        const uniqueKey = `dc-${service.id}-${activeDateString}-${Date.now()}`;
        setSavingKey(uniqueKey);

        onAddService(service, {
            due_date: activeDateString,
            is_fulfilled: true,
            quantity: 1,
            discount: 0,
            notes: '',
            uniqueKey,
        });

        const ok = await onAutoSave(true);
        if (!ok) {
            if (onRemoveService) onRemoveService(uniqueKey);
            showErrorToast(t('receptionInpatients.details.toasts.saveFailed', { defaultValue: 'Save failed' }));
        }

        setSearchQuery('');
        setShowSearch(false);
        setSavingKey(null);
    };

    const renderDayTabs = () => {
        const tabs = [];
        for (let i = 1; i <= totalDays; i++) {
            tabs.push(
                <button
                    type="button"
                    key={i}
                    onClick={() => setActiveDay(i)}
                    className={`day-tab-btn ${activeDay === i ? 'active' : ''}`}
                >
                    {t('receptionInpatients.details.dailyCare.day', { count: i, defaultValue: `Day ${i}` })}
                    <span className="tab-date">{getDayDate(i)}</span>
                </button>
            );
        }
        return tabs;
    };

    if (!visitDate) {
        return (
            <div className="daily-checklist-container">
                <div className="daily-checklist-empty">
                    <p>{t('receptionInpatients.details.dailyCare.noVisitDate', { defaultValue: 'No visit date available' })}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="daily-checklist-container">
            {/* Day Tabs */}
            <div className="days-tabs-container no-scrollbar">
                {renderDayTabs()}
            </div>

            {/* Content */}
            <div className="tab-content-inner daily-checklist-content">
                {/* Sub Tabs */}
                <div className="daily-checklist-subtabs">
                    <button
                        type="button"
                        className={`daily-checklist-subtab ${activeSubTab === 'scheduled' ? 'active' : ''}`}
                        onClick={() => setActiveSubTab('scheduled')}
                    >
                        {t('receptionInpatients.details.dailyChecklist.scheduled', { defaultValue: 'SCHEDULED' })}
                        <span className="daily-checklist-count">{scheduledServices.length}</span>
                    </button>
                    <button
                        type="button"
                        className={`daily-checklist-subtab ${activeSubTab === 'fulfilled' ? 'active' : ''}`}
                        onClick={() => setActiveSubTab('fulfilled')}
                    >
                        {t('receptionInpatients.details.dailyChecklist.fulfilled', { defaultValue: 'FULFILLED' })}
                        <span className="daily-checklist-count">{fulfilledServices.length}</span>
                    </button>
                </div>

                {/* Header row */}
                <div className="daily-checklist-header-row">
                    <div className="daily-checklist-title">
                        {t('receptionInpatients.details.dailyChecklist.title', { defaultValue: 'Daily Checklist' })}{' '}
                        <span className="daily-checklist-date">{activeDateString}</span>
                    </div>

                    {activeSubTab === 'fulfilled' && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <button
                                type="button"
                                className="daily-checklist-secondary-btn"
                                onClick={() => setShowSpecialRequests(true)}
                                disabled={!specialRequestsNote}
                                title={
                                    specialRequestsNote
                                        ? t('receptionInpatients.specialRequests.view', { defaultValue: 'View special requests' })
                                        : t('receptionInpatients.specialRequests.empty', { defaultValue: 'No special requests were added.' })
                                }
                            >
                                <i className="fas fa-clipboard" />
                                {t('receptionInpatients.specialRequests.button', { defaultValue: 'Special Requests' })}
                            </button>

                            {!readOnly && (
                                <button
                                    type="button"
                                    className="daily-checklist-add-btn"
                                    onClick={() => setShowSearch(true)}
                                    disabled={!!savingKey || !activeDateString}
                                >
                                    <i className="fas fa-plus"></i>
                                    {t('receptionInpatients.details.dailyChecklist.addService', { defaultValue: 'Add Service' })}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Add fulfilled search */}
                {activeSubTab === 'fulfilled' && showSearch && !readOnly && (
                    <div className="daily-checklist-search">
                        <div className="daily-checklist-search-inputWrap">
                            <i className="fas fa-search"></i>
                            <input
                                autoFocus
                                type="text"
                                value={searchQuery}
                                placeholder={t('receptionInpatients.details.dailyChecklist.searchPlaceholder', { defaultValue: 'Start typing to search services...' })}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setShowSearch(false);
                                        setSearchQuery('');
                                    }
                                }}
                                disabled={!!savingKey}
                            />
                        </div>

                        {searchQuery && (
                            <div className="daily-checklist-search-results">
                                {filteredSearchOptions.map(svc => (
                                    <button
                                        type="button"
                                        key={svc.id}
                                        className="daily-checklist-search-result"
                                        onClick={() => handleSelectFulfilledService(svc)}
                                        disabled={!!savingKey}
                                    >
                                        <span className="name">{isRTL ? svc.service_name_ar : svc.service_name_en}</span>
                                        <span className="meta">{svc.service_code}</span>
                                    </button>
                                ))}

                                {filteredSearchOptions.length === 0 && (
                                    <div className="daily-checklist-search-noResults">
                                        {t('common.noResults', { defaultValue: 'No results' })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Lists */}
                {activeSubTab === 'scheduled' && (
                    <div className="daily-checklist-list">
                        {scheduledServices.length === 0 ? (
                            <div className="daily-checklist-empty">
                                <i className="fas fa-check-circle"></i>
                                <p>{t('receptionInpatients.details.dailyChecklist.noScheduled', { defaultValue: 'No scheduled services for this day.' })}</p>
                            </div>
                        ) : (
                            scheduledServices.map((s) => {
                                const identifier = getIdentifier(s);
                                const isSavingThis = savingKey === identifier;
                                return (
                                    <div key={identifier} className="daily-checklist-item">
                                        <div className="daily-checklist-item-left">
                                            <div className="daily-checklist-item-name">
                                                {isRTL ? s.service_name_ar : s.service_name_en}
                                            </div>
                                            {s.service_code && (
                                                <div className="daily-checklist-item-meta">{s.service_code}</div>
                                            )}
                                            {s.notes && s.notes.trim() !== '' && (
                                                <div className="daily-checklist-item-notes">
                                                    {t('receptionInpatients.details.services.table.notes', { defaultValue: 'Additional Notes' })}: {s.notes}
                                                </div>
                                            )}
                                        </div>

                                        <div className="daily-checklist-item-right">
                                            <button
                                                type="button"
                                                className="daily-checklist-completed-btn"
                                                onClick={() => handleCompleted(s)}
                                                disabled={readOnly || !!savingKey}
                                            >
                                                <i className={isSavingThis ? 'fas fa-spinner fa-spin' : 'fas fa-check'}></i>
                                                {t('receptionInpatients.details.dailyChecklist.completed', { defaultValue: 'Completed' })}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {activeSubTab === 'fulfilled' && (
                    <div className="daily-checklist-list">
                        {fulfilledServices.length === 0 ? (
                            <div className="daily-checklist-empty">
                                <i className="fas fa-clipboard-list"></i>
                                <p>{t('receptionInpatients.details.dailyChecklist.noFulfilled', { defaultValue: 'No fulfilled services for this day.' })}</p>
                            </div>
                        ) : (
                            fulfilledServices.map((s) => {
                                const identifier = getIdentifier(s);
                                return (
                                    <div key={identifier} className="daily-checklist-item fulfilled">
                                        <div className="daily-checklist-item-left">
                                            <div className="daily-checklist-item-name">
                                                <i className="fas fa-check-circle"></i>{' '}
                                                {isRTL ? s.service_name_ar : s.service_name_en}
                                            </div>
                                            {s.service_code && (
                                                <div className="daily-checklist-item-meta">{s.service_code}</div>
                                            )}
                                            {s.notes && s.notes.trim() !== '' && (
                                                <div className="daily-checklist-item-notes">
                                                    {t('receptionInpatients.details.services.table.notes', { defaultValue: 'Additional Notes' })}: {s.notes}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            <SpecialRequestsReadOnlyModal
                isOpen={showSpecialRequests}
                onClose={() => setShowSpecialRequests(false)}
                note={specialRequestsNote}
            />
        </div>
    );
};

export default DailyChecklistTab;
